<template>
  <div class="plant-history-tile" :class="[itemData.class]" data-cy="plant-history-tile">
    <div class="plant-history-tile__header">
      <div class="plant-history-tile__icon" :class="itemData.iconClass">
        <font-awesome-icon :icon="itemData.icon" />
      </div>
      <div class="plant-history-tile__main">
        <div class="plant-history-tile__title">
          {{ itemData.title }}
          <BadgeIndicator v-if="itemData.badge" type="default" icon="tasks" :text="itemData.badge" />
        </div>
        <div class="plant-history-tile__meta" :class="{ 'history-link': itemData.showLink }" @click="itemData.showLink && onClick()">
          {{ itemData.details }}
        </div>
        <div class="plant-history-tile__date">
          <font-awesome-icon icon="fa-solid fa-calendar" /> {{ formattedDate }}
        </div>
      </div>
    </div>

    <div class="plant-history-tile__body">
      <!-- Visit Inspection Details -->
      <template v-if="item.Type === EnumPlantHistoryType.VisitInspection">
        <div class="detail-row">
          <span class="detail-label">Visit Type:</span>
          <span class="detail-value">{{ visitTypeName }}</span>
        </div>
        <div class="detail-row">
          <span class="detail-label">RFR:</span>
          <span class="detail-value">{{ item.RfrName }}</span>
        </div>
        <div v-if="item.Rc" class="detail-row">
          <span class="detail-label">RC:</span>
          <span class="detail-value">{{ item.Rc }}</span>
        </div>
      </template>

      <!-- Visit Comment Details -->
      <template v-else-if="item.Type === EnumPlantHistoryType.VisitComment">
        <div v-if="item.WhoAdded" class="detail-row">
          <span class="detail-label">Comment By:</span>
          <span class="detail-value">{{ item.WhoAdded }}</span>
        </div>
        <div class="detail-row">
          <span class="detail-label">Comment:</span>
          <span class="detail-value">"{{ truncatedComment }}"</span>
        </div>
      </template>

      <!-- Ingredient Line Item Details -->
      <template v-else-if="item.Type === EnumPlantHistoryType.IngredientLineItem">
        <div class="detail-row">
          <span class="detail-label">Ingredient:</span>
          <span class="detail-value">{{ item.IngredientName }}</span>
        </div>
        <div v-if="item.SourceCompany" class="detail-row">
          <span class="detail-label">Source Company:</span>
          <span class="detail-value">{{ item.SourceCompany }}</span>
        </div>
        <div v-if="item.Brand" class="detail-row">
          <span class="detail-label">Brand:</span>
          <span class="detail-value">{{ item.Brand }}</span>
        </div>
        <div v-if="usedIn1StatusText" class="detail-row">
          <span class="detail-label">Status:</span>
          <span class="detail-value">{{ usedIn1StatusText }}</span>
        </div>
      </template>

      <!-- Product Line Item Details -->
      <template v-else-if="item.Type === EnumPlantHistoryType.ProductLineItem">
        <div class="detail-row">
          <span class="detail-label">Product:</span>
          <span class="detail-value">{{ item.ProductName }}</span>
        </div>
        <div v-if="item.Brand" class="detail-row">
          <span class="detail-label">Brand:</span>
          <span class="detail-value">{{ item.Brand }}</span>
        </div>
        <div v-if="item.LabelName" class="detail-row">
          <span class="detail-label">Label:</span>
          <span class="detail-value">{{ item.LabelName }}</span>
        </div>
        <div v-if="labelStatusText" class="detail-row">
          <span class="detail-label">Status:</span>
          <span class="detail-value">{{ labelStatusText }}</span>
        </div>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import type { PlantHistoryDisplay } from "@/Domain/PlantHistoryDisplay";
import { EnumPlantHistoryType } from "@/Domain/EnumPlantHistoryType";
import { EnumVisitType } from "@/Domain/EnumVisitType";
import { EnumMerchStatus } from "@/Domain/EnumMerchStatus";
import { EnumUsedIn1Status } from "@/Domain/EnumUsedIn1Status";
import BadgeIndicator from "@/shared/ui/BadgeIndicator.vue";
import enumHelper from "@/shared/helpers/enumHelper";
import moment from "moment";

const props = defineProps<{
  item: PlantHistoryDisplay;
}>();

const emits = defineEmits<{
  (e: "itemClicked", item: PlantHistoryDisplay): void;
}>();

const itemData = computed(() => {
  switch (props.item.Type) {
    case EnumPlantHistoryType.VisitInspection:
      return {
        class: "type-visit",
        iconClass: "icon-visit",
        icon: "fa-solid fa-clipboard-list",
        title: `${visitTypeName.value} Visit`,
        details: `Visit ID: ${props.item.Id}`,
        badge: props.item.ReportStatus,
        showLink: props.item.ReportStatus !== "Paper",
      };
    case EnumPlantHistoryType.VisitComment:
      return {
        class: "type-comment",
        iconClass: "icon-comment",
        icon: "fa-solid fa-comment",
        title: "Visit Comment Added",
        details: `Visit ID: ${props.item.VisitId}`,
        showLink: true,
      };
    case EnumPlantHistoryType.IngredientLineItem:
      return {
        class: "type-ingredient",
        iconClass: "icon-ingredient",
        icon: "fa-solid fa-seedling",
        title: "Ingredient Job Submitted",
        details: `Job ID: ${props.item.JobId}`,
        showLink: true,
      };
    case EnumPlantHistoryType.ProductLineItem:
      return {
        class: "type-product",
        iconClass: "icon-product",
        icon: "fa-solid fa-box",
        title: "Product Job Submitted",
        details: `Job ID: ${props.item.JobId}`,
        showLink: true,
      };
    default:
      return {};
  }
});

const formattedDate = computed(() => {
  const date = props.item.Date || props.item.ActualVisitDate;
  return date ? moment(date).format("MMMM D, YYYY") : "N/A";
});

const visitTypeName = computed(() => {
  return enumHelper.splitByCapitalLetter(EnumVisitType[props.item.VisitType]);
});

const truncatedComment = computed(() => {
  const comment = props.item.Comment || "";
  return comment.length > 150 ? comment.substring(0, 150) + "..." : comment;
});

const labelStatusText = computed(() => {
  if (props.item.LabelStatus === null) return "";
  return enumHelper.splitByCapitalLetter(EnumMerchStatus[props.item.LabelStatus]);
});

const usedIn1StatusText = computed(() => {
  if (props.item.UsedIn1Status === null) return "";
  return enumHelper.splitByCapitalLetter(EnumUsedIn1Status[props.item.UsedIn1Status]);
});

const onClick = function () {
  emits("itemClicked", props.item);
};
</script>

<style lang="scss">
.plant-history-tile {
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  transition: all 0.2s;
  display: flex;
  flex-wrap: wrap;

  &:hover {
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.05);
  }

  &__header {
    padding: 16px;
    display: flex;
    align-items: flex-start;
    gap: 12px;
    flex: 1;
    min-width: 300px;
  }

  &__icon {
    width: 40px;
    height: 40px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 18px;
    flex-shrink: 0;

    &.icon-visit {
      background: #dbeafe;
    }

    &.icon-comment {
      background: #f3e8ff;
    }

    &.icon-ingredient {
      background: #d1fae5;
    }

    &.icon-product {
      background: #fef3c7;
    }
  }

  &__main {
    flex: 1;
    min-width: 0;
  }

  &__title {
    font-size: 16px;
    font-weight: 600;
    margin-bottom: 4px;
    display: flex;
    gap: 8px;
    align-items: center;
  }

  &__meta {
    font-size: 13px;
    color: #6b7280;
    margin-bottom: 8px;

    &.history-link {
      text-decoration: underline;
      color: #0056b3;
      cursor: pointer;
    }
  }

  &__date {
    font-size: 14px;
    color: #374151;
    margin-bottom: 8px;
  }

  &__body {
    padding: 16px;
    border-left: 1px solid #e5e7eb;
    flex: 1;
    min-width: 300px;

    .detail-row {
      display: flex;
      gap: 8px;
      margin-bottom: 8px;
      font-size: 14px;
      flex-wrap: wrap;

      &:last-child {
        margin-bottom: 0;
      }
    }

    .detail-label {
      font-weight: 500;
      color: #6b7280;
      min-width: 100px;
      flex-shrink: 0;
    }

    .detail-value {
      color: #111827;
      flex: 1;
      min-width: 150px;
    }
  }

  @media (max-width: 768px) {
    &__header {
      min-width: 100%;
    }

    &__body {
      border-left: none;
      border-top: 1px solid #e5e7eb;
      min-width: 100%;
    }

    .detail-label {
      min-width: 80px;
    }

    .detail-value {
      min-width: 100px;
    }
  }
}
</style>
